import './App.css';
import React from 'react';
import dayjs from 'dayjs';
import qs from 'query-string';
import debounce from 'lodash/debounce';
import Cookies from 'universal-cookie';
import { connect } from 'react-redux';
import { Global, css } from '@emotion/core';
import createHttp from './utils/http';
import { withBookingSettings } from './components/BookingSettings';
import { BookingStep } from './constants';
import { bookingActions } from './actions/booking';
import { salonActions } from './actions/salon';
import { User, createUser } from './types/user';
import { generateAntdStyles, generateAntdCustomStyles } from './styles';
import { Configuration } from './types/configuration';
import { QueryParams, FetchBookingOptions } from './types/salon';
import { Spinner } from './components/Suspense';
import EmptyState from './components/EmptyState';
import PageLayout from './components/Layout';
import Booking from './components/Booking';

const http = createHttp();
const cookies = new Cookies();
const context = { cookies, http };

interface Props {
  users: User[];
  storeActiveUser: (userId: number) => void;
  updateUsersList: (users: User[]) => void;
  updateStep: (step: number) => void;
  storeQueryParams: (params: QueryParams) => void;
  storeCurrentBooking: (options: any) => void;
  bookingSettings: Configuration;
  fetchBookingById: (options: FetchBookingOptions) => Promise<any>;
}
interface States {
  customStyles: string;
  isLoading: boolean;
  queryParams: QueryParams;
}

class App extends React.Component<Props, States> {
  constructor(props) {
    super(props);
    this.state = {
      customStyles: '',
      isLoading: true,
      queryParams: {},
    };
  }

  private hideLoadingSpinner = debounce(() => {
    this.setState({ isLoading: false });
  }, 500);

  async componentDidMount() {
    const {
      users,
      updateStep,
      storeQueryParams,
      updateUsersList,
      storeActiveUser,
    } = this.props;
    const urlParams = qs.parse(window.location.search) as any;
    const {
      salonId,
      stylistId,
      date,
      primaryColor,
      withContainer,
      standalone,
      destination: redirectUrl,
      canCancel,

      // from dashboard
      dashboard,
      bookingId,
      accessToken,

      // from mini site
      userId,
      firstName,
      lastName,
      phoneNumber,
    } = urlParams;

    // store query params
    const queryParams = {
      ...urlParams,
      salonId: salonId ? Number(salonId) : undefined,
      stylistId: stylistId ? Number(stylistId) : undefined,
      date: date ? dayjs(date).toDate() : undefined,
      bookingId: bookingId ? Number(bookingId) : undefined,
      primaryColor: primaryColor ? `#${primaryColor}` : '#46b9cb',
      userId: userId ? Number(userId) : undefined,
      withContainer: !!withContainer,
      standalone: !!standalone,
      isAdmin: !!dashboard,
      canCancel: !!canCancel,
      redirectUrl,
    };
    storeQueryParams(queryParams);
    this.setState({ queryParams });

    // custom styling for antd tabs
    const antdStyles = generateAntdStyles(queryParams.primaryColor);
    const antdCustomStyles = generateAntdCustomStyles(queryParams.primaryColor);
    this.setState({
      customStyles: antdStyles + antdCustomStyles,
    });

    // booking from mini site
    if (!!userId && !!firstName && !!lastName && !!phoneNumber) {
      const bookedUser = createUser({
        user_id: Number(userId),
        first_name: firstName,
        last_name: lastName,
        mobile_phone: phoneNumber,
        can_delete: false,
        can_select_service: true,
        can_select_stylist: true,
        can_select_date_time: true,
      });
      updateUsersList([bookedUser]);
      storeActiveUser(Number(userId));
    }

    // fetch booking detail
    if (bookingId) {
      const { storeCurrentBooking, fetchBookingById } = this.props;
      const currentBooking = await fetchBookingById({ salonId, bookingId, accessToken });
      const { bookingClients } = currentBooking;

      updateUsersList(bookingClients);
      storeActiveUser(bookingClients[0].id || 1);
      storeCurrentBooking(currentBooking);

      updateStep(BookingStep.BookingDetail);
      this.hideLoadingSpinner();
    } else {
      const { date, time } = queryParams;
      const [mainUser, ...nestedUsers] = users;
      let needRefreshUsers = false;

      if (!mainUser.selectedDate && date) {
        mainUser.selectedDate = date;
        needRefreshUsers = true;
      }
      if (!mainUser.selectedTime && time) {
        mainUser.selectedTime = time;
        needRefreshUsers = true;
      }

      if (needRefreshUsers) {
        updateUsersList([mainUser].concat(nestedUsers));
      }

      updateStep(BookingStep.Users);
      this.hideLoadingSpinner();
    }
  }

  render() {
    const { bookingSettings } = this.props;
    const { customStyles, isLoading, queryParams } = this.state;
    const { salonId, salonPhone, isAdmin, standalone } = queryParams;
    const hasLoading = isLoading || !bookingSettings;

    return (
      <div className="app app--booking">
        <Global styles={css`${customStyles}`} />

        <PageLayout title="Booking" salonId={Number(salonId)} standalone={standalone}>
          {hasLoading && <Spinner />}
          {!hasLoading && bookingSettings && (
            <>
              {(isAdmin || bookingSettings.canBookOnline) && (
                <Booking bookingSettings={bookingSettings} />
              )}

              {!(isAdmin || bookingSettings.canBookOnline) && (
                <EmptyState salonPhone={salonPhone} />
              )}
            </>
          )}
        </PageLayout>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  users: state.bookingStore.users,
});
const mapDispatchToProps = dispatch => ({
  updateStep: (step: number) => dispatch(bookingActions.updateStep(step)),
  storeActiveUser: (userId: number) => dispatch(bookingActions.storeActiveUser(userId)),
  updateUsersList: (users: User[]) => dispatch(bookingActions.updateUsers(users)),
  storeQueryParams: (params) => dispatch(salonActions.storeQueryParams(params)),
  fetchBookingById: (options) => dispatch(bookingActions.fetchBookingById(options)),
  storeCurrentBooking: (options) => dispatch(bookingActions.storeCurrentBooking(options)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withBookingSettings(App, context));
